import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography, Container } from '@mui/material';
import PublisherCard from '../../components/publisherCard';
import PublisherCardSkeleton from '../../components/publisherCardSkeleton';
import usePublisher from '../../../store/models/publisher/usePublisher';
import * as styles from './styles';

const SKELETON_COUNT = 3; // matches the grid's widest breakpoint (lg: 3 columns)

function Recommended({ subscriber, onSync, onShare }) {
    const { recommendedPublishers, whatsHotPublisherIds, recommendedPublishersLoading } = usePublisher();

    // Only ever shown for a logged-in subscriber — loading or not.
    if (!subscriber) return null;
    if (!recommendedPublishersLoading && !recommendedPublishers.length) return null;

    function renderSectionHeader() {
        return (
            <Box sx={styles.sectionHeader}>
                <Box>
                    <Typography sx={styles.eyebrow}>
                        Recommended for you
                    </Typography>
                    <Typography variant="h2" sx={styles.title}>
                        Picks based on what you{' '}
                        <Box component="span" sx={styles.gradientCool}>
                            already follow
                        </Box>
                    </Typography>
                    <Typography sx={styles.subtitle}>
                        We think these publishers might interest you, based on what you like already, your location and popularity!
                    </Typography>
                </Box>
            </Box>
        );
    }

    function renderGrid() {
        return (
            <Box sx={styles.grid}>
                {recommendedPublishers.map((publisher) => (
                    <PublisherCard
                        key={publisher.pubId}
                        publisher={publisher}
                        hotRank={whatsHotPublisherIds[String(publisher.pubId)]}
                        onSync={onSync}
                        onShare={onShare}
                    />
                ))}
            </Box>
        );
    }

    function renderSkeletonGrid() {
        return (
            <Box sx={styles.grid}>
                {Array.from({ length: SKELETON_COUNT }, (_, i) => (
                    <PublisherCardSkeleton key={i} />
                ))}
            </Box>
        );
    }

    // Only skeleton while there's nothing cached yet — recommendedPublishersLoading
    // also flips true on every remount (getRecommendedPublishers runs unconditionally
    // on every initPublisherData() call), and we don't want to re-flash the skeleton
    // over cards that are already loaded and valid.
    const showSkeleton = recommendedPublishersLoading && !recommendedPublishers.length;

    return (
        <Box component="section" sx={styles.section}>
            <Container maxWidth="xl">
                {renderSectionHeader()}
                {showSkeleton ? renderSkeletonGrid() : renderGrid()}
            </Container>
        </Box>
    );
}

export default observer(Recommended);
